import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Crown, Coins, Trophy, CaretDoubleRight, Plus, Minus, Lightning, ChartBar, ListChecks } from "@phosphor-icons/react";
import { fetchContent, trackEvent, FALLBACK_CONTENT_V5, FALLBACK_CONTENT_V6, FALLBACK_CONTENT_V7 } from "@/lib/content";

const EASE = [0.22, 1, 0.36, 1];
const CYAN = "#e3b45c";
const MAGENTA = "#e06b80";
const YELLOW = "#f2d193";
const REDUCED = typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

function useCountUp(value, delay = 0.2, duration = 900) {
  const [n, setN] = useState(REDUCED ? value : 0);
  useEffect(() => {
    if (REDUCED) { setN(value); return; }
    let raf;
    const timer = setTimeout(() => {
      const t0 = performance.now();
      const tick = (t) => {
        const p = Math.min((t - t0) / duration, 1);
        setN(Math.round(value * (1 - Math.pow(1 - p, 3))));
        if (p < 1) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    }, delay * 1000);
    return () => { clearTimeout(timer); cancelAnimationFrame(raf); };
  }, [value, delay, duration]);
  return n;
}

function parseMoney(str = "") {
  const m = String(str).match(/^(\D*)([\d,]+)(.*)$/);
  if (!m) return { prefix: "", num: 0, suffix: str };
  return { prefix: m[1], num: parseInt(m[2].replace(/,/g, ""), 10) || 0, suffix: m[3] };
}

function MoneyVault({ value }) {
  const { prefix, num, suffix } = parseMoney(value);
  const n = useCountUp(num, 0.35, 1100);
  return (
    <span className="hud-mid-focal font-chakra font-black tracking-tight tabular-nums" data-testid="hero-money">
      {prefix}{n.toLocaleString()}{suffix}
    </span>
  );
}

function Countdown({ startDate, liveWord = "Live", dayLabel = "Day", startsIn = "Starts in" }) {
  const calc = () => {
    const diff = new Date(startDate).getTime() - Date.now();
    if (isNaN(diff)) return null;
    if (diff <= 0) return "live";
    return { d: Math.floor(diff / 86400000), h: Math.floor((diff % 86400000) / 3600000), m: Math.floor((diff % 3600000) / 60000) };
  };
  const [t, setT] = useState(calc);
  useEffect(() => { setT(calc()); const id = setInterval(() => setT(calc()), 30000); return () => clearInterval(id); }, [startDate]);
  if (!t) return null;
  const pad = (x) => String(x).padStart(2, "0");
  return (
    <span className="inline-flex items-center gap-2 font-inter text-[10px] md:text-[11px] font-bold tracking-[0.24em] uppercase" style={{ color: CYAN }} data-testid="hud-countdown">
      <span className="w-1.5 h-1.5 rounded-full stage-dot" style={{ background: t === "live" ? MAGENTA : CYAN, boxShadow: `0 0 10px ${t === "live" ? MAGENTA : CYAN}` }} />
      {t === "live"
        ? <>{liveWord} <span className="font-chakra text-[#f9efe6]">{dayLabel} {Math.max(1, Math.floor((Date.now() - new Date(startDate).getTime()) / 86400000) + 1)}</span></>
        : <>{startsIn} <span className="font-chakra text-[#f9efe6]">{pad(t.d)}D:{pad(t.h)}H:{pad(t.m)}M</span></>}
    </span>
  );
}

function RuleTable({ head, rows, accent = CYAN, testId }) {
  return (
    <div className="rounded-xl border overflow-hidden" style={{ borderColor: `${accent}55` }} data-testid={testId}>
      <table className="w-full border-collapse text-left">
        <thead>
          <tr style={{ background: "linear-gradient(180deg, #3d0a1a, #160409)" }}>
            <th className="font-inter text-[9px] md:text-[10px] font-bold tracking-[0.16em] uppercase text-[#e6d7cf] px-3 md:px-4 py-2.5">{head[0]}</th>
            <th className="font-inter text-[9px] md:text-[10px] font-bold tracking-[0.16em] uppercase text-[#e6d7cf] px-3 md:px-4 py-2.5 text-right">{head[1]}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const top = i === rows.length - 1;
            return (
              <tr key={i} data-testid={`${testId}-row-${i + 1}`} style={{ background: top ? `${YELLOW}14` : i % 2 ? "rgba(227,180,92,0.04)" : "transparent", borderTop: "1px solid rgba(227,180,92,0.12)" }}>
                <td className="font-inter text-xs md:text-sm text-[#f9efe6] px-3 md:px-4 py-2 whitespace-nowrap">{r[0]}</td>
                <td className="font-chakra text-sm md:text-base px-3 md:px-4 py-2 text-right whitespace-nowrap" style={{ color: top ? YELLOW : accent, fontWeight: top ? 800 : 500 }}>{r[1]}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function GeoBlock({ data, accent, testId }) {
  const rows = (data?.tiers || []).map((t) => [`${t.count}+`, `$${t.amount.toLocaleString()}`]);
  return (
    <div data-testid={testId}>
      <div className="flex items-center gap-2">
        <span className="w-1.5 h-1.5" style={{ background: accent, boxShadow: `0 0 8px ${accent}` }} />
        <span className="font-chakra uppercase tracking-[0.12em] text-sm md:text-base" style={{ color: accent }}>{data?.name}</span>
      </div>
      <p className="mt-0.5 font-inter text-[8px] md:text-[9px] font-bold tracking-[0.14em] uppercase text-[#c9a9a6]/80">{data?.subtitle}</p>
      <div className="mt-3"><RuleTable head={["Convs ≥", "Bonus"]} rows={rows} accent={accent} testId={`${testId}-tbl`} /></div>
    </div>
  );
}

const CHAPTERS = [
  { id: "podium", num: "01", label: "Boss Podium", icon: Crown },
  { id: "ladders", num: "02", label: "Reward Ladders", icon: ChartBar },
  { id: "payout", num: "03", label: "Payout Math", icon: ListChecks },
];

export default function RulebookPage({ variant = "v5" }) {
  const FB = variant === "v6" ? FALLBACK_CONTENT_V6 : variant === "v7" ? FALLBACK_CONTENT_V7 : FALLBACK_CONTENT_V5;
  const [content, setContent] = useState(FB);
  const [loaded, setLoaded] = useState(false);
  const [booted, setBooted] = useState(false);
  const [tab, setTab] = useState("podium");
  const [celebrate, setCelebrate] = useState(false);
  const [openRule, setOpenRule] = useState(0);
  const consoleRef = useRef(null);

  useEffect(() => {
    let active = true;
    setLoaded(false);
    fetchContent(variant)
      .then((data) => { if (active) setContent({ ...FB, ...data }); })
      .catch(() => { if (active) setContent(FB); })
      .finally(() => { if (active) setLoaded(true); });
    return () => { active = false; };
  }, [variant]);

  // Konami easter egg
  useEffect(() => {
    const seq = ["ArrowUp", "ArrowUp", "ArrowDown", "ArrowDown", "ArrowLeft", "ArrowRight", "ArrowLeft", "ArrowRight", "b", "a"];
    let idx = 0;
    const onKey = (e) => {
      const k = e.key.length === 1 ? e.key.toLowerCase() : e.key;
      idx = k === seq[idx] ? idx + 1 : (k === seq[0] ? 1 : 0);
      if (idx === seq.length) { idx = 0; setCelebrate(true); trackEvent("konami", variant); setTimeout(() => setCelebrate(false), 2800); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [variant]);

  const h = content.hero || {};
  const ranks = content.ranks || {};
  const ladders = content.ladders || {};
  const breakdown = content.breakdown || {};
  const steps = content.steps || {};
  const rankItems = ranks.items || [];
  const isGeo = !!ladders.premium;

  const selectChapter = (id) => {
    setTab(id);
    trackEvent("chapter_open", id);
    consoleRef.current?.scrollIntoView({ behavior: REDUCED ? "auto" : "smooth", block: "start" });
  };
  const login = () => trackEvent("login_click", variant);

  const rankMeta = [
    { grad: "linear-gradient(135deg, #FFE600, #FF7A00)", ring: YELLOW, tag: "CHAMPION" },
    { grad: "linear-gradient(135deg, #E0E6ED, #8A99AD)", ring: "#CBD5E1", tag: "RUNNER-UP" },
    { grad: "linear-gradient(135deg, #FF9E54, #A84914)", ring: "#FF9E54", tag: "THIRD" },
  ];

  return (
    <div className="casino-royale tct-bg min-h-screen relative overflow-x-hidden text-[#f9efe6]">
      <div className="tct-grid absolute inset-0 pointer-events-none" />

      {/* PRESS START boot */}
      <AnimatePresence>
        {!booted && (
          <motion.div
            className="fixed inset-0 z-[90] flex flex-col items-center justify-center bg-[#160409] cursor-pointer"
            initial={{ opacity: 1 }} exit={{ opacity: 0, scale: 1.05 }} transition={{ duration: 0.5 }}
            onClick={() => setBooted(true)} data-testid="press-start-overlay"
          >
            <div className="tct-grid absolute inset-0 opacity-60" />
            <motion.p className="font-inter text-xs tracking-[0.4em] uppercase text-[#c9a9a6] mb-6" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
              {h.brandLead} <span style={{ color: CYAN }}>{h.brandAccent}</span> · Insert Coin
            </motion.p>
            <motion.button
              className="font-chakra text-3xl sm:text-5xl font-black tracking-widest uppercase tct-amber-pulse"
              style={{ color: YELLOW }}
              animate={REDUCED ? {} : { opacity: [1, 0.35, 1] }} transition={{ duration: 1.1, repeat: Infinity }}
              data-testid="press-start-btn"
            >
              ▶ Press Start
            </motion.button>
            <p className="mt-8 font-inter text-[10px] tracking-[0.3em] uppercase text-[#8f6d6d]">Click anywhere to enter</p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Konami celebration */}
      <AnimatePresence>
        {celebrate && (
          <motion.div className="fixed inset-0 z-[95] flex items-center justify-center pointer-events-none"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 50% 50%, rgba(224,107,128,0.25), transparent 60%)" }} />
            <motion.p className="font-chakra text-4xl sm:text-6xl font-black uppercase tracking-widest"
              style={{ color: YELLOW, textShadow: `0 0 30px ${MAGENTA}, 0 0 60px ${CYAN}` }}
              initial={{ scale: 0.5, rotate: -6 }} animate={{ scale: 1.1, rotate: 0 }} transition={{ type: "spring", stiffness: 200 }}>
              +30 LIVES · GG!
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Sticky top HUD */}
      <header className="sticky top-0 z-50 backdrop-blur-md border-b" style={{ background: "rgba(13,3,8,0.9)", borderColor: "rgba(227,180,92,0.22)" }}>
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 shrink-0">
            <span className="w-2.5 h-2.5 rotate-45" style={{ background: CYAN, boxShadow: `0 0 10px ${CYAN}` }} />
            <span className="font-chakra text-xs sm:text-sm uppercase tracking-[0.14em]">{h.brandLead} <span style={{ color: CYAN }}>{h.brandAccent}</span></span>
          </div>
          <nav className="hidden md:flex items-center gap-1">
            {CHAPTERS.map((c) => (
              <button key={c.id} onClick={() => selectChapter(c.id)} data-testid={`navtab-${c.id}`}
                className="px-3 py-1.5 rounded-md font-inter text-[10px] font-bold tracking-[0.16em] uppercase transition-colors"
                style={{ color: tab === c.id ? "#160409" : "#c9a9a6", background: tab === c.id ? CYAN : "transparent" }}>
                {c.num} · {c.label}
              </button>
            ))}
          </nav>
          <div className="flex items-center gap-3 shrink-0">
            <span className="hidden sm:block"><Countdown startDate={h.startDate} liveWord={h.liveWord} dayLabel={h.dayLabel} startsIn={h.startsInLabel} /></span>
            <button onClick={login} className="tct-btn px-3 sm:px-4 py-2 rounded-md font-chakra text-[10px] sm:text-xs uppercase tracking-[0.1em]" data-testid="hud-login-btn">
              {h.loginLabel}
            </button>
          </div>
        </div>
      </header>

      {!loaded ? (
        <div className="min-h-[70vh] flex items-center justify-center" data-testid="rulebook-loading">
          <span className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: `${CYAN} transparent ${CYAN} ${CYAN}` }} />
        </div>
      ) : (
        <main className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 pb-28">
          {/* Corner brackets */}
          <span className="tct-bracket border-t border-l left-2 top-2" />
          <span className="tct-bracket border-t border-r right-2 top-2" />

          {/* CHAPTER 00 — Mission (hero bento) */}
          <section className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 pt-10 md:pt-14 pb-12">
            <motion.div className="lg:col-span-7 tct-panel rounded-2xl p-6 md:p-9 relative overflow-hidden"
              initial={REDUCED ? false : { opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }}>
              <span className="font-inter text-[10px] tracking-[0.3em] uppercase text-[#c9a9a6]">Chapter 00 · The Mission</span>
              <div className="mt-4 flex items-center gap-2 font-inter text-[11px] font-bold tracking-[0.2em] uppercase" style={{ color: CYAN }} data-testid="hero-dates">
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: MAGENTA, boxShadow: `0 0 8px ${MAGENTA}` }} />{h.dates}
              </div>
              <p className="mt-5 font-chakra text-lg md:text-xl uppercase tracking-[0.12em] text-[#c9a9a6]">{h.winPre}</p>
              <div className="mt-1 text-5xl sm:text-6xl lg:text-7xl leading-none"><MoneyVault value={h.maxTotal} /></div>
              <p className="mt-5 font-inter text-sm md:text-base text-[#e6d7cf] max-w-md">{h.supporting}</p>
              <div className="mt-7 flex flex-wrap items-center gap-3">
                <button onClick={() => selectChapter("ladders")} className="tct-btn px-5 py-3 rounded-md font-chakra text-sm uppercase tracking-[0.1em] inline-flex items-center gap-2" data-testid="hero-rules-btn">
                  ▶ See The Rules <CaretDoubleRight size={16} weight="bold" />
                </button>
                <button onClick={login} className="hud-mid-login-cta px-5 py-3 rounded-md font-chakra text-sm uppercase tracking-[0.1em] inline-flex items-center gap-2" data-testid="hero-login-btn">
                  {h.loginLabel} <ArrowRight size={16} weight="bold" />
                </button>
              </div>
              <p className="mt-4 font-inter text-[11px] text-[#8f6d6d] max-w-sm">{h.ctaNote}</p>
            </motion.div>

            <div className="lg:col-span-5 flex flex-col gap-6">
              <motion.div className="tct-card rounded-2xl p-6 relative" data-testid="starter-kit-badge"
                initial={REDUCED ? false : { opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1, ease: EASE }}>
                <div className="flex items-center gap-2">
                  <Coins size={22} weight="fill" color={YELLOW} />
                  <span className="font-chakra text-sm uppercase tracking-[0.14em]" style={{ color: YELLOW }}>Starter Kit</span>
                </div>
                <p className="mt-3 font-inter text-sm text-[#e6d7cf]">{h.signinBonus}</p>
              </motion.div>

              <motion.div className="tct-panel rounded-2xl p-5 flex-1"
                initial={REDUCED ? false : { opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.18, ease: EASE }}>
                <span className="font-inter text-[10px] tracking-[0.28em] uppercase text-[#c9a9a6]">Select Chapter</span>
                <div className="mt-3 grid grid-cols-1 gap-2.5">
                  {CHAPTERS.map((c) => {
                    const Icon = c.icon;
                    return (
                      <button key={c.id} onClick={() => selectChapter(c.id)} data-testid={`chapter-select-${c.id}`}
                        className="group flex items-center gap-3 rounded-lg border px-4 py-3 text-left transition-all"
                        style={{ borderColor: tab === c.id ? CYAN : "rgba(227,180,92,0.25)", background: tab === c.id ? "rgba(227,180,92,0.08)" : "rgba(22,4,9,0.6)" }}>
                        <span className="font-chakra text-lg font-black" style={{ color: MAGENTA }}>{c.num}</span>
                        <Icon size={18} weight="fill" color={CYAN} />
                        <span className="font-chakra text-sm uppercase tracking-[0.08em] flex-1">{c.label}</span>
                        <ArrowRight size={14} weight="bold" className="opacity-40 group-hover:opacity-100 transition-opacity" />
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            </div>
          </section>

          {/* CONSOLE — inline chapters (no modals) */}
          <section ref={consoleRef} className="tct-panel rounded-2xl p-5 md:p-8 scroll-mt-20" data-testid="rulebook-console">
            <div className="flex flex-wrap gap-2 border-b pb-4" style={{ borderColor: "rgba(227,180,92,0.2)" }}>
              {CHAPTERS.map((c) => {
                const Icon = c.icon;
                const on = tab === c.id;
                return (
                  <button key={c.id} onClick={() => setTab(c.id)} data-testid={`console-tab-${c.id}`}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg font-chakra text-xs sm:text-sm uppercase tracking-[0.08em] transition-all"
                    style={{ color: on ? "#160409" : "#f9efe6", background: on ? CYAN : "rgba(227,180,92,0.06)", border: `1px solid ${on ? CYAN : "rgba(227,180,92,0.25)"}`, boxShadow: on ? `0 0 22px ${CYAN}66` : "none" }}>
                    <Icon size={16} weight="fill" /> {c.num} · {c.label}
                  </button>
                );
              })}
            </div>

            <AnimatePresence mode="wait">
              <motion.div key={tab} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.35, ease: EASE }} className="pt-6">

                {tab === "podium" && (
                  <div data-testid="chapter-podium">
                    <h3 className="font-chakra text-2xl md:text-3xl uppercase tracking-[0.06em]">Boss Podium <span style={{ color: MAGENTA }}>· Hall of Fame</span></h3>
                    <p className="mt-1 font-inter text-sm text-[#c9a9a6] max-w-2xl">{ranks.subtitle}</p>
                    <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-5">
                      {rankItems.map((r, i) => {
                        const meta = rankMeta[i] || rankMeta[2];
                        return (
                          <motion.div key={i} data-testid={`podium-rank-${i + 1}`}
                            className="tct-card rounded-2xl p-6 text-center relative"
                            initial={REDUCED ? false : { opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08, ease: EASE }}
                            style={{ order: i === 0 ? 2 : i === 1 ? 1 : 3 }}>
                            <div className="mx-auto w-14 h-14 rounded-full flex items-center justify-center" style={{ background: meta.grad, boxShadow: `0 0 24px ${meta.ring}66` }}>
                              <Trophy size={26} weight="fill" color="#160409" />
                            </div>
                            <p className="mt-3 font-inter text-[10px] font-bold tracking-[0.22em] uppercase" style={{ color: meta.ring }}>{meta.tag}</p>
                            <p className="font-chakra text-xl uppercase tracking-[0.06em]">#{r.rank} {r.label}</p>
                            <p className="mt-2 font-chakra text-3xl md:text-4xl font-black" style={{ color: YELLOW, textShadow: `0 0 18px ${YELLOW}55` }}>${(r.amount || 0).toLocaleString()}</p>
                            {r.qualifier ? <p className="mt-1 font-inter text-[11px] text-[#c9a9a6]">{r.qualifier}</p> : null}
                          </motion.div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {tab === "ladders" && (
                  <div data-testid="chapter-ladders">
                    <h3 className="font-chakra text-2xl md:text-3xl uppercase tracking-[0.06em]">Reward Ladders <span style={{ color: MAGENTA }}>· Level Up</span></h3>
                    {ladders.subtitle ? <p className="mt-1 font-inter text-sm text-[#c9a9a6] max-w-2xl">{ladders.subtitle}</p> : null}
                    {isGeo ? (
                      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                        <GeoBlock data={ladders.premium} accent={CYAN} testId="ladder-premium" />
                        <GeoBlock data={ladders.nonPremium} accent={MAGENTA} testId="ladder-nonpremium" />
                      </div>
                    ) : (
                      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                        <div>
                          <p className="font-chakra text-sm uppercase tracking-[0.12em] mb-3" style={{ color: CYAN }}>{ladders.weekly?.title}</p>
                          <RuleTable head={[ladders.weekly?.colLabel || "Convs", "Bonus"]} rows={(ladders.weekly?.rows || []).map((r) => [r.tier, r.bonus])} accent={CYAN} testId="ladder-weekly" />
                        </div>
                        {ladders.growth ? (
                          <div>
                            <p className="font-chakra text-sm uppercase tracking-[0.12em] mb-3" style={{ color: MAGENTA }}>{ladders.growth.title}</p>
                            <RuleTable head={["Growth %", "Bonus"]} rows={(ladders.growth.rows || []).map((r) => [r.tier, r.bonus])} accent={MAGENTA} testId="ladder-growth" />
                            {ladders.growth.note ? <p className="mt-2 font-inter text-[11px] italic text-[#c9a9a6]">{ladders.growth.note}</p> : null}
                          </div>
                        ) : null}
                      </div>
                    )}
                    {ladders.daily ? (
                      <div className="mt-6 max-w-md">
                        <p className="font-chakra text-sm uppercase tracking-[0.12em] mb-3 inline-flex items-center gap-2" style={{ color: YELLOW }}><Lightning size={15} weight="fill" />{ladders.daily.title}</p>
                        <RuleTable head={["Per Day", "Bonus"]} rows={(ladders.daily.rows || []).map((r) => [r.item, r.value])} accent={YELLOW} testId="ladder-daily" />
                        {ladders.daily.note ? <p className="mt-2 font-inter text-[11px] italic text-[#c9a9a6]">{ladders.daily.note}</p> : null}
                      </div>
                    ) : null}
                  </div>
                )}

                {tab === "payout" && (
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-testid="chapter-payout">
                    <div className="lg:col-span-7">
                      <h3 className="font-chakra text-2xl md:text-3xl uppercase tracking-[0.06em]">{breakdown.title}</h3>
                      <div className="mt-5 rounded-xl border overflow-hidden" style={{ borderColor: "rgba(227,180,92,0.3)" }}>
                        {(breakdown.rows || []).map((r, i) => (
                          <div key={i} className="flex items-center justify-between gap-4 px-4 py-3 border-b" style={{ borderColor: "rgba(227,180,92,0.12)", background: i % 2 ? "rgba(227,180,92,0.03)" : "transparent" }} data-testid={`payout-row-${i + 1}`}>
                            <div>
                              <p className="font-chakra text-sm uppercase tracking-[0.05em]" style={{ color: CYAN }}>{r.label}</p>
                              <p className="font-inter text-[11px] text-[#c9a9a6]">{r.detail}</p>
                            </div>
                            <span className="font-chakra text-lg md:text-xl whitespace-nowrap" style={{ color: "#f9efe6" }}>{r.amount}</span>
                          </div>
                        ))}
                        <div className="flex items-center justify-between gap-4 px-4 py-4" style={{ background: `${MAGENTA}18` }} data-testid="payout-total">
                          <span className="font-chakra text-base uppercase tracking-[0.1em]" style={{ color: YELLOW }}>{breakdown.totalLabel || "Total"}</span>
                          <span className="font-chakra text-3xl md:text-4xl font-black" style={{ color: YELLOW, textShadow: `0 0 20px ${YELLOW}55` }}>{breakdown.total}</span>
                        </div>
                      </div>
                      {breakdown.perCustomer ? <p className="mt-3 font-inter text-sm text-[#e6d7cf]"><span style={{ color: CYAN }}>▸</span> {breakdown.perCustomer}</p> : null}
                    </div>
                    <div className="lg:col-span-5">
                      <p className="font-inter text-[10px] tracking-[0.28em] uppercase text-[#c9a9a6] mb-3">System Logs · The Fine Print</p>
                      <div className="flex flex-col gap-2">
                        {(steps.items || []).map((s, i) => {
                          const open = openRule === i;
                          return (
                            <div key={i} className="rounded-lg border overflow-hidden" style={{ borderColor: "rgba(227,180,92,0.25)" }} data-testid={`rule-${i + 1}`}>
                              <button onClick={() => setOpenRule(open ? -1 : i)} className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left">
                                <span className="font-chakra text-sm uppercase tracking-[0.05em]">{s.title}</span>
                                {open ? <Minus size={16} color={MAGENTA} /> : <Plus size={16} color={CYAN} />}
                              </button>
                              <AnimatePresence>
                                {open && (
                                  <motion.p initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }}
                                    className="px-4 pb-3 font-inter text-[12px] leading-relaxed text-[#c9a9a6] overflow-hidden">{s.desc}</motion.p>
                                )}
                              </AnimatePresence>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}

              </motion.div>
            </AnimatePresence>
          </section>
        </main>
      )}

      {/* Persistent bottom PRESS START bar */}
      {loaded && booted && (
        <motion.div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 flex items-center gap-4 sm:gap-6 rounded-full px-5 sm:px-6 py-3 backdrop-blur-md"
          initial={{ y: 80, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.4, ease: EASE }}
          style={{ background: "rgba(22,4,9,0.92)", border: `1px solid ${MAGENTA}80`, boxShadow: `0 0 22px ${MAGENTA}44` }} data-testid="sticky-hud-bar">
          <span className="hidden sm:flex items-center gap-2 font-inter text-[10px] tracking-[0.2em] uppercase text-[#c9a9a6]">
            <span className="w-1.5 h-1.5 rounded-full stage-dot" style={{ background: CYAN, boxShadow: `0 0 8px ${CYAN}` }} />
            Top Score <span className="font-chakra text-sm" style={{ color: YELLOW }}>{h.maxTotal}</span>
          </span>
          <button onClick={login} className="tct-btn px-5 py-2.5 rounded-full font-chakra text-xs sm:text-sm uppercase tracking-[0.1em] inline-flex items-center gap-2" data-testid="sticky-login-btn">
            ▶ {h.loginLabel}
          </button>
        </motion.div>
      )}
    </div>
  );
}
